// This table handles both FNOL and Policy incident configurations.
// Domain-specific columns, actions, and dialogs are toggled via feature-flag props.
import React from 'react';

import { CliveChip } from '~/components/AiChat/CliveChip/CliveChip';
import { OverflowArrayTextDisplayWithTooltip, SortableTable, Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useOrganization from '~/components/OrganizationContext';
import EditFnolButton from '~/components/SystemConfiguration/FnolConfiguration/EditFnolButton';
import PostFnolHookDialog from '~/components/SystemConfiguration/FnolConfiguration/PostFnolHookDialog/PostFnolHookDialog';
import SetInboxDialog from '~/components/SystemConfiguration/FnolConfiguration/SetInboxDialog/SetInboxDialog';
import EditClivePolicyDialog from '~/components/SystemConfiguration/PolicyConfiguration/EditClivePolicyDialog/EditClivePolicyDialog';
import EditPolicyButton from '~/components/SystemConfiguration/PolicyConfiguration/EditPolicyButton';
import { serverDateToLocal } from '~/DateTimeUtils';
import { getLobDescription } from '~/Utils/lobUtils';

import type { IncidentConfigurationSummary, IncidentConfigurationSummaryList } from './types';

const EM_DASH = '\u2014';

interface ConfigurationsTableProps {
  incidentConfigSummaries: IncidentConfigurationSummaryList;
  onEditIncidentConfig: (id: number) => void;
  trackMixpanelEvent: (incidentConfiguration: IncidentConfigurationSummary) => void;
  counterSuffix: string;
  displayName: string;
  reloadIncidentConfigSummaries: () => Promise<IncidentConfigurationSummaryList | undefined>;
  displayFNOLInboxCols?: boolean;
  displayPolicyCliveConfig?: boolean;
  displayPostFnolHook?: boolean;
  onEditCategories?: (row: IncidentConfigurationSummary) => void;
}

const ConfigurationsTable: React.FC<ConfigurationsTableProps> = ({
  incidentConfigSummaries,
  onEditIncidentConfig,
  trackMixpanelEvent,
  counterSuffix,
  displayName,
  reloadIncidentConfigSummaries,
  displayFNOLInboxCols,
  displayPolicyCliveConfig = false,
  displayPostFnolHook = false,
  onEditCategories,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { subOrganizationEnabled, subOrganizations, emailDomains, organizationId } = useOrganization();
  const { lobConfigurationsDict } = useLobConfiguration();

  const isDisplayInboxColsEnabled = displayFNOLInboxCols;

  const isPolicyCliveConfigEnabled = displayPolicyCliveConfig;

  const [inboxDialogData, setInboxDialogData] = React.useState<IncidentConfigurationSummary | null>(null);
  const [clivePolicyDialogData, setClivePolicyDialogData] = React.useState<IncidentConfigurationSummary | null>(null);
  const [postFnolHookDialogData, setPostFnolHookDialogData] = React.useState<IncidentConfigurationSummary | null>(null);
  const Counter = () => (
    <Text
      weight={Text.WEIGHTS.SEMI_BOLD}
      variant={Text.VARIANTS.SM}
    >{`${incidentConfigSummaries?.length} ${counterSuffix}`}</Text>
  );

  const getCliveStatus = (
    should_send_fnol_email_inbox_to_clive?: boolean | null,
    fnol_email_inbox_name?: string | null
  ) => {
    if (!fnol_email_inbox_name) {
      return null;
    }

    // Use the effective value calculated by the backend (considers both local and global Clive)
    return should_send_fnol_email_inbox_to_clive ? 'Active' : 'Inactive';
  };

  const getSubOrg = (sub_organization_id: number) => {
    return subOrganizations.find((so: { id: number }) => so.id === sub_organization_id);
  };

  function getEmailDomain(subOrgId?: number | null): string | undefined {
    if (!subOrgId) {
      return getEmailDomainNoSubOrgs();
    }
    return getEmailDomainForSubOrg(subOrgId);
  }

  // Returns the email domain for an organization that has no sub-organizations, or get the organization domain for a sub-organization that has no specific email-domain defined
  function getEmailDomainNoSubOrgs(): string {
    return emailDomains.find((d: { sub_organization_ids: number[] }) => d.sub_organization_ids.length === 0)?.domain;
  }

  // Returns the email domain for a subOrg, find emailDomain in subOrgs object.
  const getEmailDomainForSubOrg = (subOrgId: number): string | undefined => {
    const subOrg = getSubOrg(subOrgId);
    if (subOrg?.email_domain_id) {
      const domainObj = emailDomains.find((d: { id: number }) => d.id === subOrg.email_domain_id);
      if (domainObj) return domainObj.domain;
    }

    return getEmailDomainNoSubOrgs();
  };

  const actionsCell = ({ onEdit }: { onEdit: (row: IncidentConfigurationSummary) => void }) => ({
    id: 'actions',
    label: 'Actions',
    disableSort: true,
    width: '70px',
    specialCell: (row: IncidentConfigurationSummary) => {
      if (displayPolicyCliveConfig) {
        return (
          <EditPolicyButton
            row={row}
            onEdit={onEdit}
            trackMixpanelEvent={trackMixpanelEvent}
            onEditClivePolicy={setClivePolicyDialogData}
            isPolicyCliveConfigEnabled={isPolicyCliveConfigEnabled}
            onEditCategories={onEditCategories}
          />
        );
      }
      return (
        <EditFnolButton
          row={row}
          onEdit={onEdit}
          trackMixpanelEvent={trackMixpanelEvent}
          onEditFnolInbox={setInboxDialogData}
          onEditPostFnolHook={displayPostFnolHook ? setPostFnolHookDialogData : undefined}
          isDisplayInboxColsEnabled={isDisplayInboxColsEnabled}
        />
      );
    },
  });

  const columns = [
    {
      id: 'display_name',
      label: displayName,
    },
    {
      id: 'version',
      label: 'Version',
      width: 80,
    },
    {
      id: 'sub_organization_id',
      label: 'Sub Organization',
      isHidden: !subOrganizationEnabled,
      specialCell: ({ sub_organization_id }: { sub_organization_id: number }) => {
        const subOrg = getSubOrg(sub_organization_id);
        return (
          <div className="min-w-80">
            <OverflowArrayTextDisplayWithTooltip
              value={subOrg ? [subOrg.name] : []}
              renderItem={(label) => (
                <span key={label}>
                  <Chip label={label} size="small" />
                </span>
              )}
            />
          </div>
        );
      },
      disableSort: true,
    },
    {
      id: 'lob',
      label: 'Line of Business',
      specialCell: ({ lob }: { lob: string }) => (
        <div>
          <Chip label={getLobDescription(lob, lobConfigurationsDict)} size="small" />
        </div>
      ),
      disableSort: true,
    },
    {
      id: 'policy_categories_count',
      // "Enabled", not just "Categories": the count is of enabled rows only, so a
      // configuration whose categories are all disabled reads as none here while its
      // Categories screen still lists them.
      label: 'Enabled Categories',
      numeric: true,
      width: 160,
      // Gated on the signal that already gates the row's "Edit Categories" action rather
      // than on a prop of its own: the Policy Configuration page passes onEditCategories
      // only while the organization's POLICY_CATEGORIES flag is on, and the FNOL page,
      // which shares this table, never passes it at all.
      isHidden: !onEditCategories,
      specialCell: ({ policy_categories_count }: IncidentConfigurationSummary) => policy_categories_count || EM_DASH,
    },
    {
      id: 'last_updated_datetime',
      label: 'Last Updated',
      specialCell: ({ last_updated_datetime }: IncidentConfigurationSummary) =>
        serverDateToLocal(last_updated_datetime),
    },
    {
      id: 'last_updated_by_username',
      label: 'Updated By',
    },
    {
      id: 'inbox',
      label: 'Inbox',
      isHidden: !isDisplayInboxColsEnabled,
      specialCell: (row: IncidentConfigurationSummary) => {
        return row.fnol_email_inbox_name ? (
          <Text variant="small" weight="regular">
            {row.fnol_email_inbox_name + '@' + getEmailDomain(row.sub_organization_id)}
          </Text>
        ) : (
          <Button variant="outlined" color="primary" onClick={() => setInboxDialogData(row)}>
            Set Inbox
          </Button>
        );
      },
    },
    {
      id: 'clive_suggestions',
      label: 'Clive Suggestions',
      isHidden: !isDisplayInboxColsEnabled,
      specialCell: ({ fnol_email_inbox_name, should_send_fnol_email_inbox_to_clive }: IncidentConfigurationSummary) => {
        const status = getCliveStatus(should_send_fnol_email_inbox_to_clive, fnol_email_inbox_name);
        return status ? (
          <CliveChip content={<div className="flex items-center gap-24 text-base font-normal">{status}</div>} />
        ) : null;
      },
    },
    actionsCell({
      onEdit: ({ id }: { id: number }) => onEditIncidentConfig(id),
    }),
  ];

  return (
    <>
      <Counter />

      <SortableTable columns={columns} rows={incidentConfigSummaries} stickyHeader maxHeight="50vh" />

      {inboxDialogData && (
        <SetInboxDialog
          onClose={async () => {
            await reloadIncidentConfigSummaries();
            setInboxDialogData(null);
          }}
          domain={getEmailDomain(inboxDialogData.sub_organization_id)}
          lobName={inboxDialogData.lob}
          organization={organizationId}
          subOrg={inboxDialogData.sub_organization_id}
          emailName={inboxDialogData.fnol_email_inbox_name ? inboxDialogData.fnol_email_inbox_name.split('@')[0] : ''}
          activateClive={inboxDialogData.should_send_fnol_email_inbox_to_clive ?? false}
        />
      )}

      {postFnolHookDialogData && (
        <PostFnolHookDialog
          onClose={() => setPostFnolHookDialogData(null)}
          organizationId={organizationId}
          incidentConfigurationId={postFnolHookDialogData.id}
        />
      )}

      {clivePolicyDialogData && (
        <EditClivePolicyDialog
          onClose={async () => {
            await reloadIncidentConfigSummaries();
            setClivePolicyDialogData(null);
          }}
          subOrgId={clivePolicyDialogData.sub_organization_id}
          lobName={clivePolicyDialogData.lob}
          displayName={clivePolicyDialogData.display_name}
        />
      )}
    </>
  );
};

export default ConfigurationsTable;
